/**
 * Shared deterministic demo dataset used across Victim / Field / Command
 * when the user toggles Demo mode at the top of the shell. Keeping IDs and
 * coordinates stable means screen recordings look identical run-to-run and
 * Gemini responses don't drift between toggles.
 *
 * Centroid is downtown San Francisco so the schematic + Google map both
 * have something realistic to anchor on.
 */
const CENTER = { lat: 37.7749, lng: -122.4194 };

const stableTimestamp = (offsetMinutes) =>
  Date.UTC(2026, 4, 8, 9, 30 - offsetMinutes, 0);

const DEMO_PACKETS_RAW = [
  {
    id: "demo-RED-01",
    urgency: "RED",
    injury: "Unconscious — building collapse",
    confidence: 0.94,
    isAutoGenerated: true,
    lat: CENTER.lat + 0.0042,
    lng: CENTER.lng - 0.0061,
    createdAt: stableTimestamp(0),
    signals: "MOTION·AUDIO·VISION",
  },
  {
    id: "demo-RED-02",
    urgency: "RED",
    injury: "Severe crush injury",
    confidence: 0.91,
    isAutoGenerated: false,
    lat: CENTER.lat - 0.0037,
    lng: CENTER.lng + 0.0028,
    createdAt: stableTimestamp(2),
    signals: "MANUAL SOS",
  },
  {
    id: "demo-YEL-01",
    urgency: "YELLOW",
    injury: "Compound fracture · stable",
    confidence: 0.78,
    isAutoGenerated: true,
    lat: CENTER.lat + 0.0019,
    lng: CENTER.lng + 0.0044,
    createdAt: stableTimestamp(5),
    signals: "MOTION·AUDIO",
  },
  {
    id: "demo-YEL-02",
    urgency: "YELLOW",
    injury: "Smoke inhalation",
    confidence: 0.74,
    isAutoGenerated: true,
    lat: CENTER.lat - 0.0024,
    lng: CENTER.lng - 0.0036,
    createdAt: stableTimestamp(7),
    signals: "AUDIO·VISION",
  },
  {
    id: "demo-GRN-01",
    urgency: "GREEN",
    injury: "Walking wounded · self-reported",
    confidence: 0.62,
    isAutoGenerated: false,
    lat: CENTER.lat + 0.0011,
    lng: CENTER.lng - 0.0017,
    createdAt: stableTimestamp(10),
    signals: "MANUAL",
  },
  {
    id: "demo-GRN-02",
    urgency: "GREEN",
    injury: "Minor laceration",
    confidence: 0.58,
    isAutoGenerated: false,
    lat: CENTER.lat - 0.0015,
    lng: CENTER.lng + 0.0019,
    createdAt: stableTimestamp(12),
    signals: "MANUAL",
  },
];

/** Canonical demo rows (no flags) — stable IDs for recordings */
export const DEMO_PACKETS = DEMO_PACKETS_RAW;

/** Demo rows merged with live Firestore when Demo mode is ON */
export const DEMO_PACKETS_WITH_DEMO = DEMO_PACKETS_RAW.map((p) => ({ ...p, isDemo: true }));

export const DEMO_PEER_COUNT = 14_372;

/**
 * Stable Gemini-style analysis returned without hitting the network when the
 * user runs analysis in demo mode. The structure matches what `runDispatchAgent`
 * produces, so downstream renderers can ignore where it came from.
 */
export const DEMO_ANALYSIS = {
  critical_alert:
    "2 RED + 2 YELLOW casualties co-located in a 600m radius — dispatch closest paramedic team first.",
  analysis:
    "Two unconscious / crush victims (demo-RED-01, demo-RED-02) form the highest-priority cluster near the SoMa block. " +
    "Yellow patients are stable but in adjacent grids; route a secondary team after RED extraction. Green walking-wounded " +
    "can self-evacuate to triage staging. Mesh routing efficiency is healthy and packets are reaching the command relay.",
  zone_assignments: [
    {
      zone: "Sector G-4 · SoMa",
      team: "Paramedic Alpha (2 vehicles)",
      reason: "Closest to both RED packets; collapse audio confirmed.",
    },
    {
      zone: "Sector G-5 · Mission",
      team: "Search & rescue Bravo",
      reason: "Yellow cluster with smoke inhalation — needs respiratory triage.",
    },
    {
      zone: "Triage staging · Civic Center",
      team: "Volunteer medics",
      reason: "Green patients can self-evacuate via Market St corridor.",
    },
  ],
  estimated_casualties: 6,
  priority_order: [
    "demo-RED-01",
    "demo-RED-02",
    "demo-YEL-01",
    "demo-YEL-02",
    "demo-GRN-01",
    "demo-GRN-02",
  ],
};

/** Demo simulation injector centroid (SF) — matches canonical demo map anchor */
export const SIMULATION_CENTER = { lat: 37.7749, lon: -122.4194 };

const JITTER_MAX = 0.005;

/** Random lat/lon within ±JITTER_MAX of SIMULATION_CENTER */
export function jitterSimulationCoords() {
  const j = () => (Math.random() * 2 - 1) * JITTER_MAX;
  return {
    lat: SIMULATION_CENTER.lat + j(),
    lon: SIMULATION_CENTER.lon + j(),
  };
}

/**
 * Blueprint rows for Firestore `packets` (coords + timestamps added at write time).
 * @typedef {{ urgency: string, isAutoGenerated: boolean, injury: string, confidence: number, signals?: string }} SimPacketBlueprint
 */

/** @type {SimPacketBlueprint[]} */
export const EARTHQUAKE_SCENARIO_BLUEPRINT = [
  { urgency: "RED", isAutoGenerated: true, injury: "Unconscious — building collapse", confidence: 0.93 },
  { urgency: "RED", isAutoGenerated: true, injury: "Crush injury — trapped under debris", confidence: 0.91 },
  { urgency: "YELLOW", isAutoGenerated: false, injury: "Compound fracture", confidence: 0.77 },
  { urgency: "YELLOW", isAutoGenerated: false, injury: "Head trauma — conscious", confidence: 0.74 },
  { urgency: "GREEN", isAutoGenerated: false, injury: "Minor lacerations", confidence: 0.66 },
  { urgency: "GREEN", isAutoGenerated: false, injury: "Shock — ambulatory", confidence: 0.62 },
];

/** @type {SimPacketBlueprint[]} */
export const FLOOD_SCENARIO_BLUEPRINT = [
  { urgency: "RED", isAutoGenerated: true, injury: "Drowning — unconscious", confidence: 0.92 },
  { urgency: "RED", isAutoGenerated: true, injury: "Hypothermia — critical", confidence: 0.9 },
  { urgency: "YELLOW", isAutoGenerated: false, injury: "Water inhalation", confidence: 0.76 },
  { urgency: "YELLOW", isAutoGenerated: false, injury: "Broken arm from debris", confidence: 0.73 },
  { urgency: "GREEN", isAutoGenerated: false, injury: "Wet clothing — stable", confidence: 0.64 },
  { urgency: "GREEN", isAutoGenerated: false, injury: "Minor cuts", confidence: 0.6 },
];

/** Injuries cycled for bulk RED inject */
export const INJECT_RED_INJURIES = [
  "Cardiac event",
  "Severe bleeding",
  "Unconscious — unknown cause",
  "Crush injury",
  "Respiratory failure",
];
